<script setup>
import { onMounted, ref, computed } from 'vue'
import { format } from '@formkit/tempo'
import { useEventStore } from '@stores/event'
import { register } from 'swiper/element/bundle'
// import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, EffectFade } from 'swiper/modules'
import FeaturedEventHighlight from '@components/index/FeaturedEventHighlight.vue'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'
import Button from '@components/forms/Button.vue'

import 'swiper/css'
import 'swiper/css/effect-fade'

register()

const swiper = ref(null)
const swipertwo = ref(null)
const eventStore = useEventStore()
const currentIndex = ref(0)
const currentEvent = computed(() => eventStore.featured[currentIndex.value] ?? {})

const breakpoints = ref({
	0: {
		slidesPerView: 1,
	},
	// 375: {
	// 	slidesPerView: 1.65,
	// },
	// 512: {
	// 	slidesPerView: 2.5,
	// },
	// 640: {
	// 	slidesPerView: 2.5,
	// },
	// 768: {
	// 	slidesPerView: 3.25,
	// },
	// 1024: {
	// 	slidesPerView: 2.5,
	// },
	// 1280: {
	// 	slidesPerView: 3.25,
	// },
	// 1536: {
	// 	slidesPerView: 3.5,
	// },
})

const next = () => {
	swiper.value.swiper.slideNext()
}

const previous = () => {
	swiper.value.swiper.slidePrev()
}

onMounted(() => {
	swiper.value.swiper.on('slideChange', () => {
		swipertwo.value.swiper.slideTo(currentIndex.value)
		currentIndex.value = swiper.value.swiper.activeIndex
	})
	swiper.value.swiper.on('activeIndexChange', () => {
		swipertwo.value.swiper.slideTo(currentIndex.value)
		currentIndex.value = swiper.value.swiper.activeIndex
	})
})
</script>

<template>
	<section class="relative overflow-hidden bg-linear-to-tr from-black via-black to-logi-grey-300 pt-16 transition-colors duration-500 ease-in-out md:h-220">
		<!-- Corner Accent -->
		<div class="clip absolute -left-32 top-0 z-10 h-80 w-60 bg-logi-blue-500 opacity-50 transition-all duration-500 ease-in-out md:left-0 lg:left-0 lg:top-0 lg:opacity-100" :style="[currentEvent.accent_hex ? { backgroundColor: currentEvent.accent_hex } : null]" />
		<!-- Mobile Poster -->
		<transition name="slow-fade" mode="out-in" v-if="currentEvent.poster" :key="currentEvent.poster">
			<img class="absolute z-0 aspect-video h-full w-full opacity-40 blur-lg md:hidden" :src="currentEvent.poster" alt="" />
		</transition>
		<div class="absolute z-10 h-full w-full bg-linear-to-t from-black to-transparent md:hidden"></div>
		<!-- Content -->
		<div class="container h-full">
			<div class="grid h-full grid-cols-1 md:grid-cols-3 xl:grid-cols-2">
				<div class="relative col-span-2 h-full xl:col-span-1">
					<div class="relative flex h-full flex-col py-5 sm:py-14 md:pr-14">
						<swiper-container
							ref="swiper"
							allow-touch-move="false"
							class="relative z-20 h-full w-full"
							:modules="[EffectFade, Autoplay]"
							effect="fade"
							:fadeEffect="{ crossFade: true }"
							:speed="500"
							:autoplay="{
								delay: 5000,
								pauseOnMouseEnter: false,
							}">
							<swiper-slide v-for="event in eventStore.featured" class="flex flex-col justify-center gap-5 sm:gap-10">
								<h2 class="hidden lg:block">{{ event.name }}</h2>
								<h2 class="block lg:hidden">{{ event.name }}</h2>
								<p class="text-white/70">{{ event.short_description }}</p>
								<div class="flex flex-col rounded-md border border-white/20 shadow-lg transition-colors duration-500 ease-in-out xs:flex-row">
									<FeaturedEventHighlight icon="fas fa-calendar-days">
										<span>{{ format(event.start_at, 'MMM D, YYYY') + ' - ' }}</span>
										<span>{{ format(event.finish_at, 'MMM D, YYYY') }}</span>
									</FeaturedEventHighlight>
									<FeaturedEventHighlight :icon="event.prize_pool_icon ? event.prize_pool_icon : 'fa-solid fa-sack-dollar'">
										<div class="mt-1 flex flex-col justify-center">
											<div class="text-sm">{{ event.prize_formatted }}</div>
											<div class="text-xs text-white/60" v-if="event.prize_pool_subtext">{{ event.prize_pool_subtext }}</div>
										</div>
									</FeaturedEventHighlight>
									<FeaturedEventHighlight :icon="event.genre.icon">{{ event.genre.name }}</FeaturedEventHighlight>
								</div>
							</swiper-slide>
						</swiper-container>
						<div class="relative z-20 mt-10 md:mt-0">
							<div class="flex items-center justify-between">
								<Button class="solid" :to="{ name: 'event', params: { slug: currentEvent.slug } }" :accent="currentEvent.accent_hex">View Details</Button>
								<ul class="flex items-center gap-2">
									<li :class="['h-2 w-2 rounded-full bg-white opacity-50 transition-opacity duration-300 ease-in-out', { '!opacity-100': i == currentIndex }]" v-for="(x, i) in swiper?.swiper?.slides?.length"></li>
								</ul>
								<div class="flex text-2xl text-logi-blue-500 transition-colors duration-500 ease-in-out" :style="[currentEvent.accent_hex ? { color: currentEvent.accent_hex } : null]">
									<button class="h-10 w-10 touch-manipulation" @click="previous">
										<i class="fa-solid fa-arrow-left"></i>
									</button>
									<button class="h-10 w-10 touch-manipulation" @click="next">
										<i class="fa-solid fa-arrow-right"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="relative h-full w-full bg-black">
					<div class="absolute left-0 top-0 h-full w-[50vw] overflow-hidden bg-black">
						<swiper-container ref="swipertwo" class="h-full" effect="fade" speed="500" allow-touch-move="false">
							<swiper-slide v-for="event in eventStore.featured">
								<img class="h-full w-full object-cover object-[85%]" :src="event.background" alt="" />
							</swiper-slide>
						</swiper-container>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style>
.clip {
	clip-path: polygon(0 0, 0% 100%, 100% 0);
}
</style>
